import React from 'react';
import classNames from 'classnames';
import { Image, initCustomElement } from '@wix/image';
import styles from './Avatar.module.scss';
/**
 * Mirrors `AvatarSize` from `wix-ui-tpa/cssVars`.
 */
export const AvatarSize = {
    xxSmall: 'xxSmall',
    xSmall: 'xSmall',
    small: 'small',
    medium: 'medium',
    large: 'large',
    xLarge: 'xLarge',
};
/** `ImageLoadStates` from `wix-ui-tpa/cssVars`. Three states, not a boolean — see below. */
var ImageLoadStates;
(function (ImageLoadStates) {
    ImageLoadStates[ImageLoadStates["READY"] = 0] = "READY";
    ImageLoadStates[ImageLoadStates["LOADING"] = 1] = "LOADING";
    ImageLoadStates[ImageLoadStates["FAILURE"] = 2] = "FAILURE";
})(ImageLoadStates || (ImageLoadStates = {}));
/**
 * `Avatar/utils.ts#nameToInitials`, verbatim.
 *
 * At the default limit of 1 a multi-word name yields ONE letter, not one per word; at limit 2 it
 * takes the first and the LAST part. The literal `' '` split is upstream's too — on a double space
 * it produces an `undefined` initial and joins it as the string "undefined". Both are quirks, and
 * both are the rendered behaviour, so they stay.
 */
const nameToInitials = (name, limit = 1) => {
    if (!name) {
        return '';
    }
    if (limit < 1 || limit > 2) {
        limit = 1;
    }
    let initials = name.split(' ').map((s) => s[0]);
    if (limit === 1 && initials.length > 1) {
        initials = [initials[0]];
    }
    if (limit === 2 && initials.length > 2) {
        initials = [initials[0], initials[initials.length - 1]];
    }
    return initials.join('').toUpperCase();
};
/**
 * `WowImage/utils.ts#extractWixMediaId`, verbatim. `<wow-image>` wants the bare media id when the
 * src is a wixstatic media URL, and the full URL otherwise.
 */
const extractWixMediaId = (src) => {
    if (src) {
        const match = src.match(/static\.wixstatic\.com\/media\/([\w\d~\.\%]*)/);
        return match && match[1];
    }
    return null;
};
/**
 * Port of `Avatar` from `wix-ui-tpa/cssVars`, structured as WUT's `Avatar.base`. Three states,
 * selected as upstream does via `!isImageLoaded ? (name ? textAvatar : anonymousAvatar) : ''`:
 *
 *  - image loaded            -> the image alone
 *  - not loaded, has name    -> initials, rendered as `content: attr(data-initials)`
 *  - not loaded, no name     -> the anonymous person-icon SVG
 *
 * The image slot is `@wix/image`'s `Image` — the platform's `<wow-image>`, not a plain `<img>` —
 * rendered unconditionally, as `Avatar.base` does; even with no `src` it yields a `<wow-image>`
 * holding a hidden `<img>`. It rasterises differently from an `<img>`, so this is a visible
 * difference rather than an implementation detail, and `initCustomElement()` in the mount effect is
 * what registers the element.
 *
 * Kept as one component rather than a compound `{Root, Image, Fallback}`, so call sites are
 * unchanged.
 */
export const Avatar = ({ className, src, name, size = AvatarSize.medium, 'data-hook': dataHook, }) => {
    /*
     * `typeof window === 'undefined' && src` starts an avatar-with-src as READY *during SSR*, which
     * is upstream's and is load-bearing: server markup then carries the image visible and no
     * initials. Seeding LOADING instead puts the initials fallback behind a `visibility: hidden`
     * image in the SSR HTML — invisible to any hydrated test, and a permanent diff in the widget's
     * SSR snapshot.
     */
    const [imageStatus, setImageStatus] = React.useState(typeof window === 'undefined' && src ? ImageLoadStates.READY : ImageLoadStates.LOADING);
    const isImageLoaded = imageStatus === ImageLoadStates.READY;
    const prevSrc = React.useRef(src);
    /* `Avatar.base` does exactly this on mount: the platform only upgrades `<wow-image>` once the
       custom element is registered. */
    React.useEffect(() => {
        initCustomElement();
    }, []);
    /*
     * Upstream re-enters LOADING only when the src changed AND the previous attempt had FAILED, so a
     * src change after a successful load leaves the old image on screen while the new one decodes
     * rather than flashing the initials. The dependency really is `[imageStatus]`: the effect compares
     * `src` against a ref rather than watching it.
     */
    React.useEffect(() => {
        const isFailedLoadingImage = imageStatus === ImageLoadStates.FAILURE;
        if (prevSrc.current !== src) {
            prevSrc.current = src;
            if (isFailedLoadingImage) {
                setImageStatus(ImageLoadStates.LOADING);
            }
        }
    }, [imageStatus]); // eslint-disable-line react-hooks/exhaustive-deps
    const imageSrc = extractWixMediaId(src) || src;
    /* `lettersLimit` (1) and `textType` (initials) are WUT's defaults, and the only combination
       this package uses — the props are gone, the behaviour is upstream's. */
    const contentText = !isImageLoaded && name ? nameToInitials(name) : '';
    const fallbackClass = isImageLoaded
        ? undefined
        : name
            ? styles.textAvatar
            : styles.anonymousAvatar;
    return (React.createElement("div", { 
        /* The generated sheet uses WUT's own state class names (`size-medium` etc.), so the prefix
           has to be emitted — without it the avatar has no width, height or font-size at all. */
        className: classNames(styles.root, styles[`size-${size}`], fallbackClass, className), "data-hook": dataHook, "data-part": "avatar", "data-content-type": src ? 'image' : name ? 'text' : 'placeholder', "data-img-loaded": isImageLoaded, "data-initials": contentText, title: name },
        React.createElement(Image, { className: styles.image, alt: name ?? '', uri: imageSrc ?? '', name: imageSrc ?? '', displayMode: "fill", 
            /* Upstream passes both as `undefined`; the platform measures the container. */
            width: undefined, height: undefined, onLoad: () => setImageStatus(ImageLoadStates.READY), 
            /* FAILURE, not back to LOADING — the distinction is what lets the effect above know a
               later src change is a retry worth re-arming. */
            onError: () => setImageStatus(ImageLoadStates.FAILURE), imageStyles: isImageLoaded ? {} : { visibility: 'hidden' } }),
        !src && React.createElement("span", { className: styles.srOnly }, name)));
};
//# sourceMappingURL=Avatar.js.map